import React, { useEffect, useRef } from "react";
import { Box, Typography, Grid, Container } from "@mui/material";
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { emailicon, locationicon, phoneicon } from "../../assets";

gsap.registerPlugin(ScrollTrigger);

const contactData = [
  {
    icon: emailicon,
    title: "EMAIL",
    details: ["contact@kollagroup.com"],
    onClick: () => (window.location.href = "mailto:contact@kollagroup.com"),
  },
  {
    icon: locationicon,
    title: "LOCATION",
    details: ["Corporate Office : Plot No. #1175,", "Road No. 56, Jubilee Hills,", "Hyderabad-500033"],
    onClick: () =>
      window.open(
        "https://www.google.com/maps/dir/?api=1&destination=17.427224680123377,78.40797826625209&travelmode=driving",
        "_blank"
      ),
  },
  {
    icon: phoneicon,
    title: "CONTACT",
    details: ["+91 799 599 9771"],
    onClick: () => (window.location.href = "tel:+917995999771"),
  },
];

const ContactSection = () => {
  const sectionRef = useRef(null);
  const cardsRef = useRef<(HTMLDivElement | null)[]>([]);

  useEffect(() => {
    // Section Fade-in Animation
    gsap.fromTo(
      sectionRef.current,
      { opacity: 0, y: 50 },
      {
        opacity: 1,
        y: 0,
        duration: 1.2,
        ease: "power2.out",
        scrollTrigger: {
          trigger: sectionRef.current,
          start: "top 80%",
          toggleActions: "play none none reverse",
        },
      }
    );

    // Cards Staggered Animation
    gsap.fromTo(
      cardsRef.current,
      { opacity: 0, y: 30 },
      {
        opacity: 1,
        y: 0,
        duration: 0.8,
        ease: "power2.out",
        stagger: 0.3, // Delay between each card animation
        scrollTrigger: {
          trigger: sectionRef.current,
          start: "top 75%",
          toggleActions: "play none none reverse",
        },
      }
    );
  }, []);

  return (
    <Box
      ref={sectionRef} // ✅ Animation applied to the whole section
      sx={{
        position: "relative",
        width: "100%",
        backgroundColor: "#000",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        overflow: "hidden",
        px: { xs: 1, md: 4 },
        py: { xs: 3, md: 4 },
      }}
    >
      <Box sx={{ width: "100%", color: "white", py: { xs: 1, md: 4 }, px: { xs: 1, md: 2 } }}>
        <Container maxWidth="xl">
          {/* Section Title */}
          <Box sx={{ textAlign: "center", mb: 4 }}>
            <Typography
              variant="h3"
              sx={{
                fontWeight: "600",
                fontSize: "28px",
                background: "linear-gradient(90deg, #bf742c,rgb(252, 243, 202), #c58038)",
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
                display: "inline-block",
                fontFamily: "LEMON MILK",
              }}
            >
              CONTACT US
            </Typography>
          </Box>

          {/* Contact Cards */}
          <Box sx={{ width: "100%", display: "flex", justifyContent: "center" }}>
            <Grid container spacing={4} justifyContent="center">
              {contactData.map(({ icon, title, details, onClick }, index) => (
                <Grid item key={index} xs={12} sm={6} md={4} sx={{ display: "flex", justifyContent: "center" }}>
                  <Box
                ref={(el: HTMLDivElement | null) => (cardsRef.current[index] = el)}
                onClick={onClick}
                    sx={{
                      background: "linear-gradient(180deg, #0D0D0D 0%, #111 100%)",
                      borderRadius: "12px",
                      padding: "24px",
                      textAlign: "center",
                      minWidth: { xs: "100%", sm: "280px" },
                      maxWidth: "360px",
                      borderTop: "2px solid #BA6F2A",
                      borderRight: "2px solid #D6BF95",
                      borderBottom: "2px solid #BA6F2A",
                      borderLeft: "2px solid #D6BF95",
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      justifyContent: "center",
                      gap: "12px",
                      boxShadow: "0px 4px 10px rgba(0,0,0,0.2)",
                      transition: "transform 0.3s ease-in-out, box-shadow 0.3s ease-in-out",
                      cursor: "pointer",
                      "&:hover": {
                        transform: "scale(1.05)",
                        boxShadow: "0px 8px 20px rgba(0,0,0,0.3)",
                      },
                      "&:active": {
                        transform: "scale(0.95)",
                        boxShadow: "0px 2px 5px rgba(0,0,0,0.2)",
                      },
                    }}
                  >
                    {/* Icon */}
                    <Box component="img" src={icon} alt={title} sx={{ width: "90px", height: "90px", padding: "12px" }} />

                    {/* Title */}
                    <Typography variant="h6" sx={{ fontWeight: "600", color: "white", fontSize: "18px" }}>
                      {title}
                    </Typography>

                    {/* Details */}
                    {details.map((line, index) => (
                      <Typography key={index} sx={{ color: "#B3B3B3", fontSize: "14px", lineHeight: "1" }}>
                        {line}
                      </Typography>
                    ))}
                  </Box>
                </Grid>
              ))}
            </Grid>
          </Box>
        </Container>
      </Box>
    </Box>
  );
};

export default ContactSection;
